import { CHECK_TIMEOUT_MS } from '../consts/HealthProbeConstants'
import { HealthStatus, TimeoutCheckResult } from '../types/HealthProbeTypes'

/**
 * Executes a health check function with a timeout and returns the result.
 * Tracks response time and handles timeout errors gracefully.
 *
 * @param name - Name of the service/endpoint being checked (used in error messages)
 * @param checkFn - Async function that performs the actual health check
 * @param timeoutMs - Maximum time to wait for the check to complete (default: CHECK_TIMEOUT_MS)
 * @returns Promise resolving to a CheckResult with status, message, and response time
 *
 * @example
 * ```typescript
 * const result = await checkWithTimeout('My Service', async () => {
 *   await fetch('https://api.example.com/health')
 * })
 * ```
 */
export const checkWithTimeout = async <T>(
    name: string,
    checkFn: () => Promise<T>,
    timeoutMs: number = CHECK_TIMEOUT_MS,
): Promise<TimeoutCheckResult> => {
    const startTime = Date.now()

    try {
        const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error(`Timeout after ${timeoutMs}ms`)), timeoutMs)
        })

        await Promise.race([checkFn(), timeoutPromise])
        const responseTime = Date.now() - startTime

        if (responseTime > 5000) {
            console.warn(`${name} check took ${responseTime}ms`)
        }

        return {
            name,
            status: HealthStatus.HEALTHY,
            message: `${name} is reachable`,
            responseTime,
        }
    } catch (error) {
        const responseTime = Date.now() - startTime
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'

        return {
            name,
            status: HealthStatus.UNHEALTHY,
            message: `${name} check failed`,
            responseTime,
            error: errorMessage,
        }
    }
}
